import { useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SmithersProVenueField } from "@/components/smithers/smithers-provenue-field"
import { SMITHERS_BULK_PROPERTIES, toSmithersBulkUpdatePatch, validateSmithersBulkUpdatePatch, type SmithersBulkProperty } from "@/lib/smithers-bulk-update"
import type { SmithersProVenueSelection } from "@/types/provenue-instance"
import type { SmithersBulkUpdatePatch } from "@/types/smithers-device"

interface SmithersBulkUpdateBarProps {
  selectedCount: number
  onSave: (patch: SmithersBulkUpdatePatch) => Promise<void>
  onCancel: () => void
  isSaving: boolean
  error?: string
}

export function SmithersBulkUpdateBar({ selectedCount, onSave, onCancel, isSaving, error }: SmithersBulkUpdateBarProps) {
  const [property, setProperty] = useState<SmithersBulkProperty>()
  const [value, setValue] = useState("")
  const [useDefault, setUseDefault] = useState(false)
  const [selection, setSelection] = useState<SmithersProVenueSelection>({ mode: "global" })
  const descriptor = SMITHERS_BULK_PROPERTIES.find((item) => item.key === property)
  let patch: SmithersBulkUpdatePatch | undefined
  let validationError: string | undefined
  if (property) {
    try {
      const next = toSmithersBulkUpdatePatch(property, value, useDefault, selection)
      validateSmithersBulkUpdatePatch(next)
      patch = next
    } catch (error) { validationError = error instanceof Error ? error.message : "Enter a valid value" }
  }

  return (
    <form role="region" aria-label="Bulk configuration editor" className="flex flex-col gap-4 border-b border-border bg-primary/5 px-4 py-3" onSubmit={(event) => {
      event.preventDefault()
      if (!isSaving && selectedCount > 0 && patch) void onSave(patch)
    }}>
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="secondary" aria-live="polite">{selectedCount} device{selectedCount === 1 ? "" : "s"} selected</Badge>
        <p className="text-sm text-muted-foreground">Change one property for the selected devices. Other settings will be kept.</p>
      </div>
      <fieldset disabled={isSaving} className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="smithers-bulk-property">Property</Label>
          <Select value={property ?? ""} disabled={isSaving} onValueChange={(key) => {
            const next = SMITHERS_BULK_PROPERTIES.find((item) => item.key === key)
            if (!next) return
            setProperty(next.key); setValue(""); setUseDefault(false); setSelection({ mode: "global" })
          }}>
            <SelectTrigger id="smithers-bulk-property"><SelectValue placeholder="Select property…" /></SelectTrigger>
            <SelectContent><SelectGroup>{SMITHERS_BULK_PROPERTIES.map((item) => <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>)}</SelectGroup></SelectContent>
          </Select>
        </div>
        {descriptor && descriptor.type !== "provenue" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="smithers-bulk-value">{descriptor.label}</Label>
            {descriptor.type === "vendor" ? (
              <Select value={value} disabled={useDefault || isSaving} onValueChange={setValue}>
                <SelectTrigger id="smithers-bulk-value"><SelectValue placeholder="Select vendor…" /></SelectTrigger>
                <SelectContent><SelectGroup><SelectItem value="ProVenue">ProVenue</SelectItem><SelectItem value="Orion">Orion</SelectItem></SelectGroup></SelectContent>
              </Select>
            ) : <Input id="smithers-bulk-value" type={descriptor.type === "number" ? "number" : "text"} value={value} disabled={useDefault} min={descriptor.type === "number" ? 0 : undefined} max={descriptor.type === "number" ? property === "matchThreshold" ? 2147483647 : Number.MAX_SAFE_INTEGER : undefined} step={descriptor.type === "number" ? 1 : undefined} onChange={(event) => setValue(event.target.value)} aria-invalid={Boolean(validationError && value)} aria-describedby="smithers-bulk-value-help" />}
            <div className="flex items-center gap-2">
              <Checkbox id="smithers-bulk-default" checked={useDefault} onCheckedChange={(checked) => setUseDefault(checked === true)} />
              <label htmlFor="smithers-bulk-default" className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground">Use default</label>
            </div>
            <p id="smithers-bulk-value-help" className="text-sm text-muted-foreground">{validationError && value ? validationError : descriptor.type === "text" ? "An empty value is saved as an empty override." : "Use default restores inheritance."}</p>
          </div>
        )}
        {descriptor?.type === "provenue" && <div className="grid gap-4 md:col-span-2 md:grid-cols-2"><SmithersProVenueField value={selection} onChange={setSelection} disabled={isSaving} /></div>}
      </fieldset>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={isSaving || selectedCount === 0 || !patch}>{isSaving ? "Saving…" : "Save Changes"}</Button>
        <Button type="button" variant="outline" disabled={isSaving} onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  )
}
